/**
 * ELIO Board 블루투스 직결 (Web Bluetooth, NUS).
 * PC 블루투스로 동글 없이 토이에 붙는다. 동글이 하던 일을 그대로 한다:
 *   USB 형식 명령(EXECUTE 0x01 / SENSOR_CFG 0xF0) 을 받아 BLE 명령(cmd_power 0x10 / cmd_io 0x11 /
 *   cmd_set_sensor_cfg 0xF4) 으로 바꾸고 50ms 마다 보낸다. 토이 cmd_sensor(0xB1) 를 받아 status 에 채운다.
 * 동글과 다른 점 하나: get_config · sensor_cfg 를 TCP 가 아니라 **UDP** 로 보낸다.
 *   ESP32 토이는 TCP 조각을 길이 접두 없이 바로 cmd 로 읽어(elio-esp32/main/proto/elio_dispatch.c:282)
 *   동글식 TCP [1+len][cmd] 를 cmd=길이 로 오해한다. UDP 는 두 토이 모두 cmd 그대로 처리
 *   (nRF mobilian/library/ble_command.c:21, elio_dispatch.c udp_rcv_cb). 근거: docs/task-wbs/codex/…-resp-T1 §3
 *   TCP 수신(get_config 응답)은 ACK 해 줘야 하므로 ElioBlePacket 이 받는다.
 * 정본: USBDongle-ESP32/main/commander/m_cmd.c (on_execute_power · on_sensor_cfg · power_timer_task ·
 *   on_ble_conn_change · on_ble_udp_rcv), proto/elio_beacon.h
 * ElioSerial 과 같은 모양(connect/disconnect/send/status/env/connected) — index.js 는 통로를 모른다.
 */

const {ElioBlePacket} = require('./ElioBlePacket');
const {CMD_SENSOR_CFG} = require('./ElioPacket');

const NUS_SERVICE = '6e400001-b5a3-f393-e0a9-e50e24dcca9e';
const NUS_WRITE = '6e400002-b5a3-f393-e0a9-e50e24dcca9e'; // 동글 → 토이 (write without response)
const NUS_NOTIFY = '6e400003-b5a3-f393-e0a9-e50e24dcca9e'; // 토이 → 동글 (notify)

const PERIOD_MS = 50; // m_cmd.c:45 POWER_PERIOD_MS · :47 PACKET_RUN_MS
const MAX_INFLIGHT = 4; // GATT 쓰기 대기열 상한 — 넘으면 "지금 못 보냄"(UDP 는 버림, TCP 는 다음 run 재시도)

const CMD_EXECUTE = 0x01;
const BLE_CMD = {GET_CONFIG: 0x00, POWER: 0x10, IO: 0x11, SENSOR: 0xB1, SET_SENSOR_CFG: 0xF4};
const KEEP = -127; // app_usb_power_t 의 "변경 안 함" (m_cmd.c:103)

// USB EXECUTE power[i] → BLE 상태 칸. 순서 = m_cmd.c:102-113
const POWER_SLOTS = ['dc1', 'dc2', 'servo1', 'servo2', 'v3', 'v5', 'io0', 'io1', 'io2', 'io3'];
const POWER_FIELDS = POWER_SLOTS.slice(0, 6);
// ElioSerial.status 와 같은 이름
const STATUS_KEY = {dc1: 'DC1',
    dc2: 'DC2',
    servo1: 'SV1',
    servo2: 'SV2',
    v3: 'V3',
    v5: 'V5',
    io0: 'IO1',
    io1: 'IO2',
    io2: 'IO3',
    io3: 'IO4'};
const slotStatusKey = k => STATUS_KEY[k];
const IO_FIELDS = POWER_SLOTS.slice(6);

/**
 * 주기 호출기. 브라우저에선 Web Worker 타이머로 돈다 — 크롬은 창이 가려진 페이지의 setInterval 을
 * 1000ms 로 늦추는데(2026-10-31 실측: 보임 51ms / 가려짐 1000~1010ms), 토이는 500ms 명령 공백이면
 * 비상정지 후 다음 명령에 다시 움직인다(nRF ble_toy_car/App/main.c:143-148) → "멈췄다 갑자기 움직임".
 * Worker 가 없는 곳(node 시험)은 setInterval.
 * @param {function} fn 부를 함수
 * @param {number} ms 주기
 * @returns {function} 멈추는 함수
 */
const startTicker = function (fn, ms) {
    if (typeof Worker === 'undefined') {
        const id = setInterval(fn, ms);
        return () => clearInterval(id);
    }
    const url = URL.createObjectURL(new Blob([`setInterval(() => postMessage(0), ${ms});`],
        {type: 'text/javascript'}));
    const worker = new Worker(url);
    worker.onmessage = fn;
    return () => {
        worker.terminate();
        URL.revokeObjectURL(url);
    };
};

/**
 * 0 으로 채운 출력 상태.
 * @returns {object} slot 이름 → 값
 */
const zeroOutputs = function () {
    return POWER_SLOTS.reduce((o, k) => {
        o[k] = 0;
        return o;
    }, {});
};

/**
 * [cmd][int16 LE ...] 바이트열.
 * @param {number} cmd BLE cmd
 * @param {Array<number>} values int16 값들
 * @returns {Uint8Array} 바이트열
 */
const int16Command = function (cmd, values) {
    const b = new Uint8Array(1 + (values.length * 2));
    const v = new DataView(b.buffer);
    b[0] = cmd;
    values.forEach((x, i) => v.setInt16(1 + (i * 2), x, true));
    return b;
};

/**
 * 단순 명령 [cmd][data] (UDP 로 보냄).
 * @param {number} cmd BLE cmd
 * @param {Uint8Array} [data] 데이터
 * @returns {Uint8Array} 바이트열
 */
const command = function (cmd, data) {
    const d = data || new Uint8Array(0);
    const b = new Uint8Array(1 + d.length);
    b[0] = cmd;
    b.set(d, 1);
    return b;
};

/**
 * scratch 1B 센서 flag(bit0 sonic, bit1 line1, bit2 line2) → 4B mask LE.
 * sensor_t 값이 같은 비트라 그대로 쓴다 (elio_beacon.h:69-71).
 * @param {number} mask sensor mask
 * @returns {Uint8Array} 4B
 */
const maskBytes = function (mask) {
    const b = new Uint8Array(4);
    new DataView(b.buffer).setUint32(0, mask >>> 0, true);
    return b;
};

class ElioBle {
    constructor () {
        this._device = null;
        this._char = null;
        this._stopTicker = null;
        this._inflight = 0;
        this._chain = Promise.resolve();
        this._out = zeroOutputs();
        this._sensorMask = 0;
        this.connected = false;
        this.onConnect = null;
        this.onDisconnect = null;
        this.status = POWER_SLOTS.map(slotStatusKey).concat(['SONIC', 'LINE1', 'LINE2'])
            .reduce((o, k) => {
                o[k] = 0;
                return o;
            }, {});
        this.env = {TEMPERATURE: 0, HUMIDITY: 0, WATER: 0}; // B2 는 farm 만 보냄 — 지금은 0 유지

        this._packet = new ElioBlePacket({
            write: p => this._write(p),
            onTcp: () => {}, // get_config 응답 등 — 지금 쓰는 곳 없음 (동글도 LOG only, m_cmd.c:269-274)
            onUdp: p => this._onUdp(p)
        });
        this._onGattDisconnected = () => this._closed();
        this._onNotify = e => {
            const v = e.target.value;
            this._packet.receive(new Uint8Array(v.buffer, v.byteOffset, v.byteLength));
        };
    }

    async connect () {
        if (typeof navigator === 'undefined' || !navigator.bluetooth) {
            throw new Error('Web Bluetooth 미지원 브라우저 (Chrome/Edge 필요)');
        }
        this._device = await navigator.bluetooth.requestDevice({
            filters: [{namePrefix: 'ELIO'}, {services: [NUS_SERVICE]}],
            optionalServices: [NUS_SERVICE]
        });
        this._device.addEventListener('gattserverdisconnected', this._onGattDisconnected);
        const server = await this._device.gatt.connect();
        const service = await server.getPrimaryService(NUS_SERVICE);
        this._char = await service.getCharacteristic(NUS_WRITE);
        const notify = await service.getCharacteristic(NUS_NOTIFY);
        notify.addEventListener('characteristicvaluechanged', this._onNotify);
        await notify.startNotifications();
        this._opened();
    }

    // 끊기 전에 0 출력을 한 번 확실히 보낸다 — nRF 는 끊기면 default 출력으로 돌아가고(codex T1 §5) 0 이 아닐 수 있다.
    async disconnect () {
        if (this.connected) {
            this._out = zeroOutputs();
            this._tick();
            await this._chain;
        }
        if (this._device && this._device.gatt.connected) {
            this._device.gatt.disconnect(); // → gattserverdisconnected → _closed()
            return;
        }
        this._closed();
    }

    /**
     * USB 형식 페이로드 (ElioSerial.send 와 같은 입력). [0x30][cmd][data...]
     * @param {Uint8Array} data 페이로드
     */
    send (data) {
        const cmd = data[1];
        if (cmd === CMD_EXECUTE) this._execute(data.subarray(2));
        else if (cmd === CMD_SENSOR_CFG) this._sensorCfg(data[2]);
    }

    // m_cmd.c:102-113 on_execute_power — int8, -127 = 변경 안 함.
    // 끝 3B(use_sonic/line1/line2) 는 scratch-vm 이 항상 0 → 동글 F2 latch 와 같은 결과가 되도록 무시.
    _execute (power) {
        POWER_SLOTS.forEach((k, i) => {
            const v = (power[i] << 24) >> 24;
            if (i < power.length && v !== KEEP) this._out[k] = v;
        });
        this._mirrorStatus();
    }

    // m_cmd.c:154-181 on_sensor_cfg (1B) → :94-99 apply_sensor_mask (TCP)
    _sensorCfg (flag) {
        const mask = flag & 0x07;
        if (mask === this._sensorMask) return;
        this._sensorMask = mask;
        if (this.connected) this._packet.udpSend(command(BLE_CMD.SET_SENSOR_CFG, maskBytes(mask)));
    }

    // m_cmd.c:398-419 on_ble_conn_change(connected)
    _opened () {
        this._packet.reset();
        this._inflight = 0;
        this.connected = true;
        this._packet.udpSend(command(BLE_CMD.GET_CONFIG)); // nRF: 식별 완료 → 500ms 명령 감시 켜짐
        if (this._sensorMask) this._packet.udpSend(command(BLE_CMD.SET_SENSOR_CFG, maskBytes(this._sensorMask)));
        this._stopTicker = startTicker(() => this._tick(), PERIOD_MS);
        if (this.onConnect) this.onConnect();
    }

    // 끊김 = 안전 정지 (m_cmd.c:413-415)
    _closed () {
        if (this._stopTicker) this._stopTicker();
        this._stopTicker = null;
        if (this._device) this._device.removeEventListener('gattserverdisconnected', this._onGattDisconnected);
        this._device = null;
        this._char = null;
        this._out = zeroOutputs();
        this._mirrorStatus();
        const was = this.connected;
        this.connected = false;
        if (was && this.onDisconnect) this.onDisconnect();
    }

    // m_cmd.c:330-349 power_timer_task + :352-357 packet_run_task
    _tick () {
        this._packet.udpSend(int16Command(BLE_CMD.POWER, POWER_FIELDS.map(k => this._out[k])));
        this._packet.udpSend(int16Command(BLE_CMD.IO, IO_FIELDS.map(k => this._out[k])));
        this._packet.run();
    }

    // GATT 쓰기는 한 번에 하나 — 사슬로 줄 세운다. 대기열이 차면 false (m_cmd.c:306 ble_write_cb 와 같은 뜻).
    _write (p) {
        if (!this._char || this._inflight >= MAX_INFLIGHT) return false;
        this._inflight++;
        const ch = this._char;
        this._chain = this._chain
            .then(() => ch.writeValueWithoutResponse(p))
            .catch(e => console.warn('ELIO BLE write 실패:', e)) // eslint-disable-line no-console
            .then(() => {
                this._inflight--;
            });
        return true;
    }

    // m_cmd.c:276-286 on_ble_udp_rcv — cmd_sensor: flag u32, sonic_cm u16, line1 u16, line2 u16 (elio_beacon.h:139-144)
    _onUdp (p) {
        if (p[0] !== BLE_CMD.SENSOR || p.length < 11) return;
        const v = new DataView(p.buffer, p.byteOffset, p.byteLength);
        this.status.SONIC = v.getUint16(5, true);
        // ElioSerial 과 같은 논리값: raw 0 = 감지 → 1
        this.status.LINE1 = v.getUint16(7, true) === 0 ? 1 : 0;
        this.status.LINE2 = v.getUint16(9, true) === 0 ? 1 : 0;
    }

    _mirrorStatus () {
        POWER_SLOTS.forEach(k => {
            this.status[slotStatusKey(k)] = this._out[k];
        });
    }
}

module.exports = ElioBle;
module.exports.int16Command = int16Command;
module.exports.command = command;
module.exports.maskBytes = maskBytes;
